import type { TableItem } from "~/interface/TableItem"
import type { TableBilling } from "~/interface/TableBilling"
import type { BillingStrategy, BillingOpts } from "./BillingStrategy"
import { discountLineFn } from "./discountLine"
import { roundMoney } from "~/utils/money"

/**
 * Divana (legacy shop 4970) billing rule — ported from the old
 * Food/Scripts/app/helper.v2.js `toPrinter` engine.
 *
 * Some catalog groups are typed "Product" (e.g. retail goods whose price already
 * includes VAT). Those lines carry `skipService = true` (resolved at build time
 * from the catalog Type — see pos.products.isBillingSkipServiceSubItem). The rule:
 *
 *   - "Product" (skipService) lines  → VAT-inclusive & service-exempt: they count
 *     toward the grand total but are EXCLUDED from both the service-charge base and
 *     the exclusive-VAT base (they contribute 0 added service and 0 added VAT).
 *   - Everything else ("Food")       → normal: forms the service & exclusive-VAT base.
 *
 * Only service base + exclusive-VAT (mode 2) base differ from the default pipeline;
 * discount, card, rounding, and the emitted tuple shape are identical. Modes 0/1
 * fall back to the default full-base behaviour (legacy Divana only ever used
 * exclusive VAT).
 *
 * ROUNDING: mirrors DefaultBillingStrategy exactly — every intermediate is snapped
 * to 2 decimals with roundMoney (the v2.4.28 fix). The two divana-only bases
 * (serviceChargeBase, vatBase) are rounded too, so a "Product" line's fractional
 * part cannot leak into the service/VAT amounts and from there into the `rounding`
 * plug.
 */
export const DivanaBillingStrategy: BillingStrategy = {
    key: "Divana",

    // This is the only strategy that branches on `item.skipService`, so it is the
    // only one that pays for resolving it — see BillingStrategy.usesSkipService.
    usesSkipService: true,

    // Legacy catalog Types this shop's groups already carry. Both contain
    // "product", so the skipService classifier tags them VAT-inclusive /
    // service-exempt exactly as intended.
    extraCatalogTypes: ["Divana Product", "DII Product"],

    calculateBilling(items: TableItem[], globalOptions: any, tableOptions: any, opts?: BillingOpts): TableBilling {
        let sum = 0

        const subTotal = roundMoney(items.reduce((acc, item) => acc + item.discountedPrice, 0));
        sum += subTotal

        const itemsDiscount = roundMoney(items.reduce((acc, item) => acc + item.dc1[2], 0));

        // Σ discountedPrice of VAT-inclusive "Product" lines — excluded from the
        // service-charge base and the exclusive-VAT base below.
        const skipServiceSubtotal = roundMoney(items.reduce((acc, item) => {
            return item.skipService ? acc + item.discountedPrice : acc
        }, 0));

        //service charge — base excludes skipService ("Product") lines
        const _serviceChargeMode = opts?.noServiceCharge ? 0 : (tableOptions?.sc?.[0] ?? globalOptions?.sc?.[0] ?? 0)
        const _serviceChargeValue = opts?.noServiceCharge ? 0 : (tableOptions?.sc?.[1] ?? globalOptions?.sc?.[1] ?? 0);
        const serviceChargeBase = roundMoney(sum - skipServiceSubtotal)
        const serviceChargeAmount = roundMoney(discountLineFn(serviceChargeBase, _serviceChargeMode, _serviceChargeValue))
        sum = roundMoney(sum + serviceChargeAmount)

        //additional (whole-bill) discount — identical to default
        const _additionalDiscountMode = tableOptions?.dc2?.[0] ?? globalOptions?.dc2?.[0] ?? 0
        const _additionalDiscountValue = tableOptions?.dc2?.[1] ?? globalOptions?.dc2?.[1] ?? 0;
        let additionalDiscountAmount = 0;
        if (_additionalDiscountMode === 2) {
            if (_additionalDiscountValue > 0) {
                const excludedSubtotal = roundMoney(items.reduce((acc, item) => {
                    const isExcluded = item.noDiscount === true || (item.dc1?.[2] || 0) !== 0
                    return isExcluded ? acc + item.discountedPrice : acc
                }, 0))
                const eligibleBase = roundMoney(sum - excludedSubtotal)
                additionalDiscountAmount = roundMoney(eligibleBase * _additionalDiscountValue / 100)
            }
        } else {
            additionalDiscountAmount = roundMoney(discountLineFn(sum, _additionalDiscountMode, _additionalDiscountValue))
        }
        sum = roundMoney(sum - additionalDiscountAmount)

        //card charge — identical to default
        const _cardChargeMode = tableOptions?.cc?.[0] ?? globalOptions?.cc?.[0] ?? 0
        const _cardChargeValue = tableOptions?.cc?.[1] ?? globalOptions?.cc?.[1] ?? 0;
        const cardChargeAmount = roundMoney(discountLineFn(sum, _cardChargeMode, _cardChargeValue))
        sum = roundMoney(sum + cardChargeAmount)

        //vat , 0 = none, 1 = inclusive, 2 = exclusive
        const _vatMode = tableOptions?.vat?.[0] ?? globalOptions?.vat?.[0] ?? 0
        const _vatValue = tableOptions?.vat?.[1] ?? globalOptions?.vat?.[1] ?? 0;
        let vatAmount = 0;

        if (_vatMode === 1) {
            // Inclusive VAT — VAT already inside price (full base; legacy Divana
            // never used this mode, kept for parity with the default pipeline).
            vatAmount = roundMoney((sum * _vatValue) / (100 + _vatValue));
        } else if (_vatMode === 2) {
            // Exclusive VAT — added on top, but ONLY on the non-"Product" base.
            // subitemForVat = sum − Σ(Product discountedPrice)  (Food + charges).
            const vatBase = roundMoney(sum - skipServiceSubtotal)
            vatAmount = roundMoney((vatBase * _vatValue) / 100);
            sum = roundMoney(sum + vatAmount);
        }

        //rounding — identical to default
        const roundingMode = tableOptions?.roundingMode ?? globalOptions?.roundingMode ?? 'none';
        let roundedSum = sum;
        let roundingAmount = 0;

        switch (roundingMode) {
            case '5/4':
                roundedSum = Math.round(sum);
                break;
            case 'up':
                roundedSum = Math.ceil(sum);
                break;
            case 'down':
                roundedSum = Math.floor(sum);
                break;
            case 'none':
            default:
                roundedSum = sum;
                break;
        }

        roundingAmount = roundMoney(roundedSum - sum);
        sum = roundMoney(roundedSum);

        const grandTotal = sum;

        return {
            grandTotal,
            rounding: roundingAmount,
            roundingMode,

            sc: [_serviceChargeMode, _serviceChargeValue, serviceChargeAmount],
            dc2: [_additionalDiscountMode, _additionalDiscountValue, additionalDiscountAmount],
            cc: [_cardChargeMode, _cardChargeValue, cardChargeAmount],
            vat: [_vatMode, _vatValue, vatAmount],
            subTotal: subTotal,
            dc1: itemsDiscount,
        }
    }
}
